import React, { useEffect, useState } from 'react'
import { Table, Button, Space, message, Modal, Form, Input, Checkbox, Tooltip, Tag, Popconfirm, Switch } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined, LockOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import axiosInstance from '@/lib/axios'

interface Role {
  id: number
  codice: string
  nome: string
  descrizione: string
  attivo: boolean
  is_sistema: boolean
  colore?: string
  created_at?: string
  updated_at?: string
  _count?: {
    users?: number
    permissions?: number
  }
}

export const RolesManager: React.FC<{ onRolesChanged?: () => void }> = ({ onRolesChanged }) => {
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [form] = Form.useForm()

  // Carica i ruoli all'apertura del componente
  useEffect(() => {
    loadRoles()
  }, [])

  const loadRoles = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/permissions/roles')
      // L'API ritorna direttamente una lista di ruoli
      const rolesData = Array.isArray(response.data) ? response.data : response.data.roles || []
      setRoles(rolesData)
    } catch (error) {
      console.error('Errore caricamento ruoli:', error)
      message.error('Errore nel caricamento dei ruoli')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingRole(null)
    form.resetFields()
    form.setFieldValue('colore', '#1890ff')
    setIsModalOpen(true)
  }

  const handleEdit = (role: Role) => {
    setEditingRole(role)
    form.setFieldsValue({
      codice: role.codice,
      nome: role.nome,
      descrizione: role.descrizione,
      attivo: role.attivo,
      is_sistema: role.is_sistema,
      colore: role.colore || '#1890ff'
    })
    setIsModalOpen(true)
  }

  const handleDelete = async (roleId: number) => {
    try {
      const response = await axiosInstance.delete(`/permissions/roles/${roleId}`)
      
      // Controlla se c'è un errore nella risposta (dipendenze)
      if (response.data.success === false) {
        const details = response.data.details || 'Non è possibile eliminare questo ruolo'
        message.error(`❌ ${details}`)
        return
      }

      message.success('Ruolo eliminato con successo')
      loadRoles()
      
      // Notifica il componente padre che i ruoli sono cambiati
      if (onRolesChanged) {
        onRolesChanged()
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'eliminazione'
      message.error(errorMsg)
      console.error('Errore eliminazione:', error)
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()

      if (editingRole) {
        // Update existing role
        await axiosInstance.put(`/permissions/roles/${editingRole.id}`, values)
        message.success('Ruolo aggiornato con successo')
      } else {
        // Create new role
        await axiosInstance.post('/permissions/roles', values)
        message.success('Ruolo creato con successo')
      }

      setIsModalOpen(false)
      setEditingRole(null)
      form.resetFields()
      loadRoles()
      
      // Notifica il componente padre che i ruoli sono cambiati
      if (onRolesChanged) {
        onRolesChanged()
      }
    } catch (error: any) {
      // Distingui tra errori di validazione del form e errori di API
      if (error.errorFields) {
        // Errore di validazione del form - Ant Design lo mostrerà sui campi
        return
      }
      
      // Errore di API o di rete
      const errorMsg = error.response?.data?.detail || error.message || 'Errore durante il salvataggio'
      message.error(errorMsg)
      console.error('Errore:', error)
    }
  }

  const columns: ColumnsType<Role> = [
    {
      title: 'Codice',
      dataIndex: 'codice',
      key: 'codice',
      width: 150,
      render: (text: string, record: Role) => (
        <Tag
          color={record.colore || '#1890ff'}
          style={{ cursor: 'pointer' }}
        >
          {text}
        </Tag>
      )
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      width: 200
    },
    {
      title: 'Descrizione',
      dataIndex: 'descrizione',
      key: 'descrizione',
      ellipsis: true
    },
    {
      title: 'Stato',
      dataIndex: 'attivo',
      key: 'attivo',
      width: 100,
      render: (attivo: boolean) => (
        <Tag color={attivo ? 'green' : 'red'}>
          {attivo ? 'Attivo' : 'Inattivo'}
        </Tag>
      )
    },
    {
      title: 'Sistema',
      dataIndex: 'is_sistema',
      key: 'is_sistema',
      width: 90,
      render: (is_sistema: boolean) => is_sistema ? (
        <Tooltip title="Ruolo di sistema: non eliminabile">
          <Tag icon={<LockOutlined />} color="volcano">Sistema</Tag>
        </Tooltip>
      ) : null
    },
    {
      title: 'Utenti',
      key: 'users',
      width: 80,
      render: (_: any, record: Role) => (
        <Tag>
          {record._count?.users || 0} utenti
        </Tag>
      )
    },
    {
      title: 'Permessi',
      key: 'permissions',
      width: 80,
      render: (_: any, record: Role) => (
        <Tag>
          {record._count?.permissions || 0} perms
        </Tag>
      )
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_: any, record: Role) => (
        <Space size="small">
          <Tooltip title="Modifica ruolo">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_sistema ? 'Ruolo di sistema: non eliminabile' : undefined}>
            <Popconfirm
              title="Eliminare ruolo?"
              description={`Eliminare "${record.nome}"?`}
              onConfirm={() => handleDelete(record.id)}
              okText="Sì, elimina"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={record.is_sistema || (record._count?.users || 0) > 0}
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={record.is_sistema || (record._count?.users || 0) > 0}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="small"
        >
          Nuovo Ruolo
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={roles}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        size="small"
        bordered
      />

      <Modal
        title={editingRole ? `Modifica Ruolo: ${editingRole.codice}` : 'Nuovo Ruolo'}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingRole(null)
          form.resetFields()
        }}
        okText="Salva"
        cancelText="Annulla"
        width="min(500px, 95vw)"
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '16px' }}
        >
          <Form.Item
            name="codice"
            label="Codice (es: ADMIN, WORKSHOP)"
            rules={[
              { required: true, message: 'Codice obbligatorio' },
              { pattern: /^[A-Z_]+$/, message: 'Solo lettere maiuscole e underscore' }
            ]}
          >
            <Input
              placeholder="ADMIN"
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Nome obbligatorio' }]}
          >
            <Input placeholder="Amministratore" />
          </Form.Item>

          <Form.Item
            name="descrizione"
            label="Descrizione"
            rules={[{ required: false, message: 'Descrizione obbligatoria' }]}
          >
            <Input.TextArea
              placeholder="Descrizione del ruolo..."
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="colore"
            label="Colore (Hex)"
            rules={[{ pattern: /^#[0-9A-F]{6}$/i, message: 'Formato hex valido (es: #1890ff)' }]}
          >
            <Input
              placeholder="#1890ff"
              type="color"
            />
          </Form.Item>

          <Form.Item
            name="attivo"
            label="Attivo"
            valuePropName="checked"
          >
            <Checkbox>Abilita questo ruolo</Checkbox>
          </Form.Item>

          <Form.Item
            name="is_sistema"
            label="Ruolo di sistema"
            valuePropName="checked"
            tooltip="I ruoli di sistema non possono essere eliminati. Usare con attenzione."
          >
            <Switch
              checkedChildren={<LockOutlined />}
              unCheckedChildren="No"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default RolesManager
